import React from "react";
import { CrewPilotModuleShell } from "../../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../../layout/pageLayout";
import { DesktopKpiStrip } from "../../shared/DesktopKpiStrip";
import { DeskEmpty, DeskPanel, DeskRowMenu, DeskTable } from "../../desktop/DeskTable";
import { formatCurrency } from "../../../helpers/estimateTotals";
import "./estimateDesktop.css";

/**
 * Desktop Location workspace: features as a table with whole-row open; location
 * summary in a side panel. Presentational — state/handlers live in
 * LocationWorkspaceScreen.
 *
 * rows: [{ id, name, sqft, total, materialsCount, labourCount }]
 */
export function LocationWorkspaceDesktop({
  name,
  parentLabel,
  jobWide,
  error,
  rows,
  total,
  editable,
  menuItems,
  onBack,
  onOpenFeature,
  onAddFeature,
  children,
}) {
  const columns = [
    {
      key: "feature",
      label: "Feature",
      width: "46%",
      primary: true,
      render: (row) => <span className="dt-strong">{row.name}</span>,
    },
    {
      key: "area",
      label: "Area",
      align: "right",
      width: "18%",
      render: (row) =>
        row.sqft > 0 ? <span className="dt-num">{row.sqft.toFixed(0)} ft²</span> : <span className="dt-muted">—</span>,
    },
    {
      key: "lines",
      label: "Lines",
      align: "right",
      width: "16%",
      render: (row) => (
        <span className="dt-muted" title="Materials / Labour lines">
          {row.materialsCount} mat · {row.labourCount} lab
        </span>
      ),
    },
    {
      key: "total",
      label: "Total",
      align: "right",
      width: "20%",
      render: (row) =>
        row.total > 0 ? <span className="dt-num">{formatCurrency(row.total)}</span> : <span className="dt-muted">—</span>,
    },
  ];

  const totalSqft = rows.reduce((sum, row) => sum + row.sqft, 0);

  return (
    <CrewPilotModuleShell
      title={name}
      subtitle={jobWide ? "Job-wide scope" : undefined}
      onBack={onBack}
      desktopBackLabel={parentLabel || "Estimate"}
      layout={PAGE_LAYOUT.COMMAND}
      compact
      headerActions={
        <div className="cdh-actions">
          {editable ? (
            <button type="button" className="dt-btn dt-btn--primary" onClick={onAddFeature}>
              + Add Feature
            </button>
          ) : null}
          <DeskRowMenu
            label="Location actions"
            items={menuItems.map((item, index) => ({ ...item, key: `m${index}`, danger: item.destructive }))}
            onSelect={(item) => {
              if (!item.disabled) item.onClick?.();
            }}
          />
        </div>
      }
    >
      {error ? <div className="est-inline-error">{error}</div> : null}

      <DesktopKpiStrip
        ariaLabel="Location overview"
        className="est-d-kpi3"
        tiles={[
          { key: "total", value: formatCurrency(total), label: "Location Total", tone: "gold" },
          { key: "feats", value: String(rows.length), label: rows.length === 1 ? "Feature" : "Features", tone: "blue" },
          { key: "area", value: `${totalSqft.toFixed(0)} ft²`, label: "Measured Area", tone: "muted" },
        ]}
      />

      <DeskPanel title="Features" className="dp--flush">
        {rows.length === 0 ? (
          <DeskEmpty title="No features yet">
            {editable ? (
              <span className="est-d-empty-cta">
                <span>Add a feature to start measuring and pricing this location.</span>
                <button type="button" className="dt-btn dt-btn--primary" onClick={onAddFeature}>
                  + Add First Feature
                </button>
              </span>
            ) : (
              "This location has no features."
            )}
          </DeskEmpty>
        ) : (
          <DeskTable
            testId="location-features-table"
            className="est-d-flat"
            caption="Location features"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            onOpen={(row) => onOpenFeature(row.id)}
            openLabel={(row) => `Open ${row.name}`}
          />
        )}
      </DeskPanel>
      {children}
    </CrewPilotModuleShell>
  );
}
